import React from "react";
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
} from "@mui/material";
import ScienceIcon from "@mui/icons-material/Science";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import SecurityIcon from "@mui/icons-material/Security";
import GavelIcon from "@mui/icons-material/Gavel";
import VerifiedIcon from "@mui/icons-material/Verified";

const sections = [
  {
    title: "Testing Methodology",
    icon: <ScienceIcon color="primary" fontSize="large" />,
    content:
      "The process begins with functional verification, where every core capability is evaluated against defined clinical and technical requirements. This confirms that the system performs exactly as intended, without deviation, under normal and edge-case conditions.Our clinical testing methodology is built on structured, evidence-driven protocols designed to reflect real healthcare conditions rather than ideal laboratory settings. Testing is conducted in progressive layers, ensuring that system behavior remains accurate, reliable, and predictable across varied clinical contexts.",
      
    },
  {
    title: "Pilot Studies",
    icon: <LocalHospitalIcon color="primary" fontSize="large" />,
    content:
      "Pilot studies serve as a critical early validation step, enabling controlled evaluation of the system in real clinical settings before broader deployment. These studies are designed to assess practical feasibility, workflow integration, and initial performance under routine care conditions.  During pilot implementation, the system is observed across defined clinical scenarios to understand usability, data reliability, and interaction with existing clinical processes. Feedback from healthcare professionals is systematically captured to identify operational gaps, training needs, and areas for refinement.  Pilot studies also help verify that the technology delivers meaningful clinical value while maintaining safety and consistency. Insights generated during this phase directly inform design optimization, risk mitigation, and readiness for larger-scale clinical validation or regulatory pathways.  By bridging laboratory testing and full-scale adoption, pilot studies reduce uncertainty and ensure that the solution is grounded in real-world clinical practice.",
  },
  {
    title: "Ethical Considerations",
    icon: <GavelIcon color="primary" fontSize="large" />,
    content:
      "Ethical responsibility is fundamental to the development and deployment of medical technology. In healthcare, technology does not merely support operations—it influences clinical judgment, patient safety, and outcomes that directly affect human lives. For this reason, ethical considerations are embedded into every stage of our work, from early system design to clinical validation and real-world implementation.  Our approach recognizes that innovation must advance in parallel with accountability. We believe that long-term impact in healthcare is achieved not by speed alone, but by earning trust through transparency, safety, and respect for the people and institutions our technology serves.",
  },
  {
    title: "Data Safety & Security",
    icon: <SecurityIcon color="primary" fontSize="large" />,
    content:
      "Data safety and security are fundamental to the reliability of clinical systems. Our platforms are designed with security embedded at the architectural level, ensuring that sensitive clinical information is protected throughout its lifecycle. Access to data is governed through strict role-based controls, secure authentication, and continuous monitoring. Information is transmitted and stored using secure protocols to preserve confidentiality and integrity. Data is collected only for defined clinical and operational purposes and handled in accordance with institutional and regulatory expectations. Through disciplined governance, audit-ready traceability, and ongoing risk management, we ensure that clinical data remains accurate, protected, and trustworthy at all times.",
  },
  {
    title: "Validation with Hospitals & Institutions",
    icon: <VerifiedIcon color="primary" fontSize="large" />,
    content:
      "Validation with hospitals and clinical institutions is central to ensuring real-world relevance and reliability. We collaborate closely with healthcare partners to evaluate system performance within authentic clinical workflows, operational constraints, and care environments. These validations assess usability, data consistency, and integration with existing clinical processes. Feedback from clinicians and institutional teams is systematically incorporated to refine functionality and address practical challenges. Working within established governance and oversight structures ensures that validation activities are ethically conducted and aligned with institutional standards. This collaborative approach strengthens clinical credibility, supports regulatory readiness, and ensures solutions are grounded in everyday healthcare practice",
  },
];

const ClinicalValidationPage = () => {
  return (
    <Box sx={{ py: { xs: 4, md: 6 }, backgroundColor: "#f9fafb" }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 5 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Clinical Validation & Testing
          </Typography>
          <Typography variant="body1" color="text.secondary" maxWidth="800px" mx="auto">
        Clinical validation is the foundation upon which safe, effective, and credible medical technologies are built. For MedTech innovations intended to influence clinical decisions, workflows, or patient outcomes, validation is not a single milestone—it is a continuous, 
        structured discipline evolves from concept to real-world deployment.<br/>
        Our clinical validation framework is designed to ensure that every system we develop is clinically meaningful, scientifically sound, ethically governed, and regulator-ready. It aligns engineering rigor with patient safety, clinician usability, and institutional accountability. This framework is embedded across the product lifecycle, shaping 
        design choices, development priorities, testing methodologies, and deployment strategies.<br/>
        This approach ensures that safety claims are defensible, transparent, and aligned with international medical device risk management principles.
          </Typography>
        </Box>

        {/* Content Sections */}
        <Grid container spacing={3}>
          {sections.map((section, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card
                elevation={2}
                sx={{
                  height: "100%",
                  borderRadius: 3,
                  transition: "0.3s",
                  "&:hover": { boxShadow: 6 },
                }}
              >
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    {section.icon}
                    <Typography variant="h6" fontWeight={600} ml={1}>
                      {section.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {section.content}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default ClinicalValidationPage;
